import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { foodService } from '../../services/foodService';

const AdminFoodOrders = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [orders, setOrders] = useState([]);
  const [cities, setCities] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    cityId: '',
    vendorId: '',
    orderStatus: '',
    dateFrom: '',
    dateTo: ''
  });

  const statusOptions = [
    { value: '', label: 'Tutti gli stati' },
    { value: 'nuovo', label: 'Nuovo' },
    { value: 'in_gestione', label: 'In Gestione' },
    { value: 'in_consegna', label: 'In Consegna' },
    { value: 'completato', label: 'Completato' },
    { value: 'annullato', label: 'Annullato' }
  ];

  const statusColors = {
    nuovo: 'bg-blue-100 text-blue-800',
    in_gestione: 'bg-yellow-100 text-yellow-800',
    in_consegna: 'bg-purple-100 text-purple-800',
    completato: 'bg-green-100 text-green-800',
    annullato: 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    nuovo: 'Nuovo',
    in_gestione: 'In Gestione',
    in_consegna: 'In Consegna',
    completato: 'Completato',
    annullato: 'Annullato'
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userRole !== 'admin') {
      navigate('/');
      return;
    }

    loadInitialData();
  }, [user, userRole, navigate]);

  useEffect(() => {
    if (cities?.length > 0) {
      loadOrders();
    }
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [citiesData, vendorsData] = await Promise.all([
        foodService?.getActiveCities(),
        foodService?.getVendorsByCity('') // Get all vendors
      ]);
      setCities(citiesData);
      setVendors(vendorsData);
      await loadOrders();
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const ordersData = await foodService?.getOrders(filters);
      setOrders(ordersData);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento degli ordini');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleViewDetails = async (order) => {
    try {
      const items = await foodService?.getOrderItems(order?.id);
      setOrderItems(items);
      setSelectedOrder(order);
      setShowDetailModal(true);
    } catch (err) {
      setError('Errore nel caricamento dei dettagli ordine');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      setError('');
      setSuccess('');
      await foodService?.updateOrderStatus(orderId, newStatus);
      setSuccess('Stato ordine aggiornato con successo');
      await loadOrders();
      
      // Update selected order if detail modal is open
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, order_status: newStatus }));
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Errore nell\'aggiornamento dello stato');
    }
  };

  const filteredVendors = filters?.cityId
    ? vendors?.filter(v => v?.city_id === filters?.cityId)
    : vendors;

  const cityOptions = [
    { value: '', label: 'Tutte le città' },
    ...cities?.map(c => ({ value: c?.id, label: c?.name }))
  ];

  const vendorOptions = [
    { value: '', label: 'Tutti i fornitori' },
    ...filteredVendors?.map(v => ({ value: v?.id, label: v?.name }))
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <div className="flex items-center justify-center h-96">
          <Icon name="Loader2" size={48} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Gestione Ordini Food - Admin Cliccami</title>
      </Helmet>
      <Header onFilterClick={() => {}} />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Gestione Ordini Food</h1>
          <p className="text-muted-foreground">Dashboard centralizzata per la gestione degli ordini</p>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-sm">{success}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Filter" size={20} />
            Filtri
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Select
              label="Città"
              value={filters?.cityId}
              onChange={(e) => handleFilterChange('cityId', e?.target?.value)}
              options={cityOptions}
            />
            <Select
              label="Fornitore"
              value={filters?.vendorId}
              onChange={(e) => handleFilterChange('vendorId', e?.target?.value)}
              options={vendorOptions}
            />
            <Select
              label="Stato"
              value={filters?.orderStatus}
              onChange={(e) => handleFilterChange('orderStatus', e?.target?.value)}
              options={statusOptions}
            />
            <Input
              type="date"
              label="Data da"
              value={filters?.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
            />
            <Input
              type="date"
              label="Data a"
              value={filters?.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
            />
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ID Ordine</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Città</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Fornitore</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Totale</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Stato</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {orders?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-muted-foreground">
                      <Icon name="Package" size={48} className="mx-auto mb-4 opacity-50" />
                      <p>Nessun ordine trovato</p>
                    </td>
                  </tr>
                ) : (
                  orders?.map(order => (
                    <tr key={order?.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                        {order?.id?.slice(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {format(new Date(order?.created_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {order?.city?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {order?.vendor?.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        <div>{order?.customer_name}</div>
                        <div className="text-xs text-muted-foreground">{order?.customer_phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-foreground">
                        €{order?.order_total?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors?.[order?.order_status]}`}>
                          {statusLabels?.[order?.order_status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(order)}
                        >
                          <Icon name="Eye" size={16} className="mr-1" />
                          Dettagli
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">Dettaglio Ordine</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">ID Ordine</p>
                  <p className="text-sm font-mono text-foreground">{selectedOrder?.id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Data</p>
                  <p className="text-sm text-foreground">
                    {format(new Date(selectedOrder?.created_at), 'dd MMMM yyyy HH:mm', { locale: it })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Città</p>
                  <p className="text-sm text-foreground">{selectedOrder?.city?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Fornitore</p>
                  <p className="text-sm text-foreground">{selectedOrder?.vendor?.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedOrder?.vendor?.phone}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="border-t border-border pt-4">
                <h3 className="text-lg font-semibold text-foreground mb-3">Dati Cliente</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Nome</p>
                    <p className="text-sm text-foreground">{selectedOrder?.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Telefono</p>
                    <p className="text-sm text-foreground">{selectedOrder?.customer_phone}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">Indirizzo</p>
                    <p className="text-sm text-foreground">{selectedOrder?.delivery_address}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">Fascia Oraria</p>
                    <p className="text-sm font-semibold text-foreground">{selectedOrder?.delivery_slot}</p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="border-t border-border pt-4">
                <h3 className="text-lg font-semibold text-foreground mb-3">Prodotti</h3>
                <div className="space-y-2">
                  {orderItems?.map(item => (
                    <div key={item?.id} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                      <div>
                        <p className="text-sm font-medium text-foreground">{item?.product_name}</p>
                        <p className="text-xs text-muted-foreground">Quantità: {item?.quantity} × €{item?.unit_price?.toFixed(2)}</p>
                      </div>
                      <p className="text-sm font-semibold text-foreground">€{item?.line_total?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-border pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Totale Prodotti</span>
                    <span className="text-foreground">€{selectedOrder?.products_total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Costo Consegna</span>
                    <span className="text-foreground">€{selectedOrder?.delivery_fee?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
                    <span className="text-foreground">Totale Ordine</span>
                    <span className="text-primary">€{selectedOrder?.order_total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder?.notes && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground mb-1">Note</p>
                  <p className="text-sm text-foreground">{selectedOrder?.notes}</p>
                </div>
              )}

              {/* Status Management */}
              <div className="border-t border-border pt-4">
                <h3 className="text-lg font-semibold text-foreground mb-3">Gestione Stato</h3>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">Stato attuale:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors?.[selectedOrder?.order_status]}`}>
                    {statusLabels?.[selectedOrder?.order_status]}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {['nuovo', 'in_gestione', 'in_consegna', 'completato', 'annullato']?.map(status => (
                    <Button
                      key={status}
                      variant={selectedOrder?.order_status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleStatusChange(selectedOrder?.id, status)}
                      disabled={selectedOrder?.order_status === status}
                    >
                      {statusLabels?.[status]}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFoodOrders;